import React, { createContext, useContext, FC, ReactNode, useState, useEffect } from "react";
import {
  useFirebaseConnection,
  ConnectionStatus,
} from "../hooks/useFirebaseConnection";
import { useQueryParams } from "../hooks/useQueryParams";
import isEmpty from "lodash/isEmpty";
import { getArchHeightData, getFootSize, buildReorderInsoleValues } from "../helpers/foot";
import { fetchEnrichedProductByHandle } from "../services/shopify-api";
import { EnrichedShopifyProduct } from "../types/shopify";
import { ArchHeightData, FootSizeData, WeightRangeData } from "../types/foot";
import { ARCH_HEIGHT_DATA_ORDERED, FOAM_PACKAGE_SHOPIFY_OPTIONS, FOAM_PACKAGE_PERSONALIZATION_SUB_TEXT, FOOT_SIZE_DATA } from "../constants/foot";

type FoamPackageShopifyOptions = typeof FOAM_PACKAGE_SHOPIFY_OPTIONS;
type FoamPackagePersonalizationSubText = typeof FOAM_PACKAGE_PERSONALIZATION_SUB_TEXT;

export interface CustomInsoleValues {
  leftFootArchHeightData: ArchHeightData | {};
  rightFootArchHeightData: ArchHeightData | {};
  leftFootSizeData: FootSizeData | {};
  rightFootSizeData: FootSizeData | {};
  leftFootLengthInMM: number;
  rightFootLengthInMM: number;
  leftFootArchHeightInMM: number;
  rightFootArchHeightInMM: number;
  weightRangeData: WeightRangeData | {};
  leftFootEngraving: string;
  rightFootEngraving: string;
  firstNameOnBox: string;
  lastNameOnBox: string;
  // Opt-in consent to save this personalization data to the customer's
  // profile. Lives in context (rather than local component state) so it
  // survives navigating away from and back to the personalization screen.
  scanDataConsent: boolean;
}

export const ConnectionSessionErrorId = {
  CLUTTERED_BACKGROUND: "clutteredBackground",
  BAD_DEPTH_DATA: "badDepthData",
  FOOT_LENGTH_OUT_OF_RANGE: "footLengthOutOfRange",
  ARCH_HEIGHT_OUT_OF_RANGE: "archHeightOutOfRange",
  RIGHT_LEFT_FOOT_LENGTH_DIFF_OUT_OF_RANGE: "rightLeftFootLengthDiffOutOfRange",
  RIGHT_LEFT_ARCH_HEIGHT_DIFF_OUT_OF_RANGE: "rightLeftArchHeightDiffOutofRange",
  UNKNOWN: "unknown",
} as const;

export type ConnectionSessionErrorIdType = (typeof ConnectionSessionErrorId)[keyof typeof ConnectionSessionErrorId];

export interface ConnectionSessionError {
  id: ConnectionSessionErrorIdType;
  message: string;
}

interface AppContextType {
  customInsoleValues: CustomInsoleValues;
  setCustomInsoleValues: React.Dispatch<React.SetStateAction<CustomInsoleValues>>;
  updateSessionRef: (data: Record<string, any>) => Promise<void>;
  // Firebase connection state
  connectionCode: string | null;
  connectionStatus: (typeof ConnectionStatus)[keyof typeof ConnectionStatus];
  connectionResult: {
    left: {
      footLengthInMM: number
      archHeightInMM: number
    }
    right: {
      footLengthInMM: number
      archHeightInMM: number
    }
  } | {} | null;
  connectionSessionError: ConnectionSessionError | null;
  connectionError: string | null;
  connectionInstruction: string | null;
  connectionCountdown: "3" | "2" | "1" | null;
  connectionScanningFoot: "left" | "right" | null;
  connectionProcessing: boolean;
  setupNewConnection: () => Promise<void>;
  productData: EnrichedShopifyProduct | null;
  setProductData: (data: EnrichedShopifyProduct | null) => void;
  productHandle: string;
  setProductHandle: (handle: string) => void;
  selectedFoamPackageOption: string;
  setSelectedFoamPackageOption: (option: string) => void;
  selectedSizeOption: string;
  setSelectedSizeOption: (option: string) => void;
  foamPackageShopifyOptions: FoamPackageShopifyOptions;
  foamPackagePersonalizationSubText: FoamPackagePersonalizationSubText;
}

const initialInsoleValues: CustomInsoleValues = {
  leftFootArchHeightData: {},
  rightFootArchHeightData: {},
  leftFootSizeData: {},
  rightFootSizeData: {},
  leftFootLengthInMM: 0,
  rightFootLengthInMM: 0,
  leftFootArchHeightInMM: 0,
  rightFootArchHeightInMM: 0,
  weightRangeData: {},
  leftFootEngraving: "",
  rightFootEngraving: "",
  firstNameOnBox: "",
  lastNameOnBox: "",
  scanDataConsent: false,
};

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: FC<AppProviderProps> = ({ children }) => {
  // Extract all query parameters from URL
  const queryParams = useQueryParams();
  const { productHandle: productHandleFromQuery, selectedFoamPackage: selectedFoamPackageFromQuery, sessionUuid: sessionUuidFromQuery, variantId: variantIdFromQuery } = queryParams;

  // Computed once (lazy initializer, not recomputed from `queryParams` on
  // every render — see isReorder below for why that matters) and shared by
  // both isReorder and customInsoleValues' initial value, rather than
  // calling buildReorderInsoleValues twice.
  const [reorderSeed] = useState(() =>
    buildReorderInsoleValues(queryParams, initialInsoleValues),
  );

  // Must stay derived from `reorderSeed` (stable after mount), not
  // recomputed from `queryParams` directly — useQueryParams() strips the
  // reorder params from the URL shortly after mount, so re-deriving this
  // from the live `queryParams` would flip it to false on the next render.
  // If that happened, the Firebase-result-sync effect below would stop
  // skipping itself the next time `result` changes (even to another empty
  // value as the Firebase hook initializes without a real session), wipe
  // leftFootArchHeightData/rightFootArchHeightData back to {}, and
  // WithActiveFirebaseSessionHOC would then redirect to "/" — i.e. the
  // reorder page would load correctly and then immediately bounce to the
  // handoff screen once the URL got cleaned up.
  const isReorder = reorderSeed !== null;

  // Lazy initializer so a reorder deep link has leftFootArchHeightData/
  // rightFootArchHeightData populated on the very first render — this must
  // land before WithActiveFirebaseSessionHOC's mount effect runs (child
  // effects fire before parent effects in the same commit), otherwise it
  // would see an empty scan and redirect to "/" before this ever got a
  // chance to seed the data via a useEffect.
  const [customInsoleValues, setCustomInsoleValues] = useState<CustomInsoleValues>(
    () => reorderSeed ?? initialInsoleValues,
  );
  const [productData, setProductData] = useState<EnrichedShopifyProduct | null>(null);
  const [productHandle, setProductHandle] = useState<string>("");
  const [selectedFoamPackageOption, setSelectedFoamPackageOption] = useState<string>(FOAM_PACKAGE_SHOPIFY_OPTIONS.superrev);
  const [selectedSizeOption, setSelectedSizeOption] = useState<string>("");
  const [foamPackageShopifyOptions, setFoamPackageShopifyOptions] = useState<FoamPackageShopifyOptions>(FOAM_PACKAGE_SHOPIFY_OPTIONS);

  // Use the Firebase connection hook with optional session UUID
  const {
    connectionState: { code, status, result, sessionError, connectionError, instruction, countdown, scanning_foot, processing },
    setupNewConnection,
    updateSessionRef,
  } = useFirebaseConnection({ initialSessionUuid: sessionUuidFromQuery || undefined });

  useEffect(() => {
    setProductHandle(productHandleFromQuery);

    const fetchProduct = async () => {
      const product = await fetchEnrichedProductByHandle(productHandleFromQuery);
      setProductData(product);
      let foamPackageShopifyOptions: FoamPackageShopifyOptions | null = null;

      // Derive foam package display labels from the API response
      if (product?.foamPackageOption?.values?.length) {
        const values = product.foamPackageOption.values;
        // elite variant will have longer name
        const sortedValues = values.sort();
        const superrevValue = sortedValues[0];
        const superrevEliteValue = sortedValues[1];
        foamPackageShopifyOptions = { superrev: superrevValue, superrev_elite: superrevEliteValue };
        setFoamPackageShopifyOptions(foamPackageShopifyOptions);
      }

      // Set foam package from query param or deduce from variantId
      if (selectedFoamPackageFromQuery && Object.values(foamPackageShopifyOptions).includes(selectedFoamPackageFromQuery)) {
        setSelectedFoamPackageOption(selectedFoamPackageFromQuery);
      } else if (variantIdFromQuery && product) {
        const variant = product.variants.find(v => v.id.toString() === variantIdFromQuery);
        const optionValue = variant?.[`option${product.foamPackageOption?.position}`];
        if (optionValue) {
          // foamPackageOption contains the foam package (e.g., "SuperRev™" or "SuperRev™ Max")
          setSelectedFoamPackageOption(optionValue);
        }
      } else {
        setSelectedFoamPackageOption(foamPackageShopifyOptions.superrev);
      }
    }
    fetchProduct();
  }, [productHandleFromQuery, selectedFoamPackageFromQuery, variantIdFromQuery]);

  // Below useEffect is only used for dev
  // Don't make it changes to it
  useEffect(() => {
    if (import.meta.env.VITE_STUB_DATA_FOR_DEVELOPMENT === 'true') {
      const fetchProduct = async () => {
        const product = await fetchEnrichedProductByHandle(productHandleFromQuery);
        setProductData(product);
      }
      fetchProduct();

      // const leftFootArchHeightInMM = ARCH_HEIGHT_DATA_ORDERED[5].lessThanOrEqualTo;
      const leftFootArchHeightInMM = 13;
      const rightFootArchHeightInMM = 45;
      // const rightFootArchHeightInMM = ARCH_HEIGHT_DATA_ORDERED[0].lessThanOrEqualTo;

      const leftFootLengthInMM = FOOT_SIZE_DATA["11"].greaterThanOrEqual;
      // const rightFootLengthInMM = FOOT_SIZE_DATA["15"].greaterThanOrEqual;
      const rightFootLengthInMM = 310.5;

      const leftFootArchHeightData = getArchHeightData(leftFootArchHeightInMM);
      const rightFootArchHeightData = getArchHeightData(rightFootArchHeightInMM);

      const leftFootSizeData = getFootSize(leftFootLengthInMM);
      const rightFootSizeData = getFootSize(rightFootLengthInMM);

      setCustomInsoleValues(prev => ({
        ...prev,
        leftFootEngraving: "AWWWWWWW",
        rightFootEngraving: "AWWWWWWW",
        leftFootLengthInMM,
        rightFootLengthInMM,
        leftFootArchHeightInMM,
        rightFootArchHeightInMM,
        leftFootArchHeightData,
        leftFootSizeData,
        rightFootArchHeightData,
        rightFootSizeData,
      }));
    }
  }, []);

  useEffect(() => {
    // Also skip for a reorder deep link: there's no live Firebase session
    // behind it, so `result` stays empty and this effect would otherwise
    // immediately wipe the values the lazy initializer just seeded above.
    if (import.meta.env.VITE_STUB_DATA_FOR_DEVELOPMENT === 'true' || isReorder) {
      return;
    }

    if (result && 'left' in result && !isEmpty(result.left)) {
      const { left: { archHeightInMM, footLengthInMM } } = result;

      const leftArchData = getArchHeightData(archHeightInMM);
      const leftFootSizeData = getFootSize(footLengthInMM);

      setCustomInsoleValues(prev => ({
        ...prev,
        leftFootLengthInMM: footLengthInMM,
        leftFootArchHeightInMM: archHeightInMM,
        leftFootArchHeightData: leftArchData,
        leftFootSizeData,
      }));
    } else {
      setCustomInsoleValues(prev => ({
        ...prev,
        leftFootLengthInMM: 0,
        leftFootArchHeightInMM: 0,
        leftFootArchHeightData: {},
        leftFootSizeData: {},
      }));
    }

    if (result && 'right' in result && !isEmpty(result.right)) {
      const { right: { archHeightInMM, footLengthInMM } } = result;

      const rightArchData = getArchHeightData(archHeightInMM);
      const rightFootSizeData = getFootSize(footLengthInMM);

      setCustomInsoleValues(prev => ({
        ...prev,
        rightFootLengthInMM: footLengthInMM,
        rightFootArchHeightInMM: archHeightInMM,
        rightFootArchHeightData: rightArchData,
        rightFootSizeData,
      }));
    }
    else {
      setCustomInsoleValues(prev => ({
        ...prev,
        rightFootLengthInMM: 0,
        rightFootArchHeightInMM: 0,
        rightFootArchHeightData: {},
        rightFootSizeData: {},
      }));
    }

  }, [result]);

  const value = {
    customInsoleValues,
    setCustomInsoleValues,
    updateSessionRef,
    // Firebase connection state
    connectionCode: code,
    connectionStatus: status,
    connectionResult: result,
    connectionSessionError: sessionError,
    connectionError,
    connectionInstruction: instruction,
    connectionCountdown: countdown,
    connectionScanningFoot: scanning_foot,
    connectionProcessing: processing,
    setupNewConnection,
    // Selected product data
    productData,
    setProductData,
    productHandle,
    setProductHandle,
    selectedFoamPackageOption,
    setSelectedFoamPackageOption,
    selectedSizeOption,
    setSelectedSizeOption,
    foamPackageShopifyOptions,
    foamPackagePersonalizationSubText: {
      [foamPackageShopifyOptions.superrev]: FOAM_PACKAGE_PERSONALIZATION_SUB_TEXT[FOAM_PACKAGE_SHOPIFY_OPTIONS.superrev],
      [foamPackageShopifyOptions.superrev_elite]: FOAM_PACKAGE_PERSONALIZATION_SUB_TEXT[FOAM_PACKAGE_SHOPIFY_OPTIONS.superrev_elite],
    } as FoamPackagePersonalizationSubText,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};
